import React from 'react';

import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

import { SPACE, RADIUS } from 'src/theme/ui-tokens';

import { Iconify } from 'src/components/iconify';

/**
 * **대화상자 껍데기 한 벌** — 제목 · 닫기 · 본문 · 아래 단추 줄. 모양의 정본은 추적 등록 창(`TrackRegisterDialog`)이다.
 *
 * 🔑 흰 바탕(`background.paper`) · `RADIUS.lg` · 안쪽 여백 `SPACE.section` · 닫기는 테두리 없는 × 하나.
 * 🔑 단추 줄은 오른쪽에 모인다 — 물러서는 것은 글자 단추(`color="inherit"`), 나아가는 것은 채운 단추 하나를
 *   **마지막에** 둔다. 채운 주 색 단추는 이 껍데기가 `primary.dark` 로 칠한다(기본 주 색은 흰 글자 대비가 낮다).
 *   되돌릴 수 없는 동작이면 `color="error"` 를 넘긴다.
 * 🔑 좁은 화면은 단추가 폭을 채워 세로로 선다 — 마지막 단추(주 동작)가 맨 위다.
 * ⚠️ 단추를 `Box`/`Stack` 으로 감싸 넘기지 마라 — 감싼 덩어리 하나가 한 단추로 취급돼 위 배치가 안 먹는다.
 *
 * @param props.title 문자열이면 `uiTitle` 굵은 제목으로 그린다 · 노드면 그대로
 * @param props.actions 아래 단추들(조각으로 넘긴다) — 없고 `actionsSx` 도 없으면 줄을 그리지 않는다
 * @param props.paperRadius `sx.borderRadius` 로 그대로 내려간다(숫자 · 문자열 · 반응형 객체)
 */
const RoundedDialog = ({
  open,
  onClose,
  title,
  actions,
  children,
  maxWidth = 'sm',
  fullWidth = true,
  scroll = 'paper',
  paperBg = 'background.paper',
  paperRadius = RADIUS.lg,
  paperPadding = SPACE.section,
  paperSx,
  showClose = true,
  closeButtonSx,
  closeIconSx,
  contentWrapper = false,
  contentBg = 'transparent',
  contentRadius = 2,
  contentPadding = 0,
  contentSx,
  headerSx,
  contentRootSx,
  actionsSx,
}: {
  open: boolean
  onClose: () => void
  title: React.ReactNode
  children?: React.ReactNode
  actions?: React.ReactNode
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false
  fullWidth?: boolean
  scroll?: 'paper' | 'body'
  paperBg?: string
  paperRadius?: number | string | Record<string, unknown>
  paperPadding?: string | number | Record<string, unknown>
  paperSx?: Record<string, unknown>
  showClose?: boolean
  closeButtonSx?: Record<string, unknown>
  closeIconSx?: Record<string, unknown>
  contentWrapper?: boolean
  contentBg?: string
  contentRadius?: number
  contentPadding?: number | Record<string, unknown>
  contentSx?: Record<string, unknown>
  headerSx?: Record<string, unknown>
  contentRootSx?: Record<string, unknown>
  actionsSx?: Record<string, unknown>
}) => (
  <Dialog
    open={!!open}
    onClose={onClose}
    maxWidth={maxWidth}
    fullWidth={fullWidth}
    scroll={scroll}
    PaperProps={{
      sx: {
        bgcolor: paperBg,
        borderRadius: paperRadius,
        p: paperPadding,
        display: 'flex',
        flexDirection: 'column',
        ...(paperSx || {}),
      },
    }}
  >
    <DialogTitle sx={{ p: 0, mb: SPACE.card, ...(headerSx || {}) }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: SPACE.row }}>
        {title ? (
          typeof title === 'string' ? (
            <Typography component="span" sx={{ typography: 'uiTitle', fontWeight: 700 }}>{title}</Typography>
          ) : (
            title
          )
        ) : <Box />}
        {showClose && (
          <IconButton
            aria-label="닫기"
            onClick={onClose}
            sx={{ ml: 'auto', mr: -1, my: -1, color: 'text.secondary', ...(closeButtonSx || {}) }}
          >
            <Iconify icon="mingcute:close-line" width={20} sx={closeIconSx} />
          </IconButton>
        )}
      </Box>
    </DialogTitle>

    <DialogContent sx={{ p: 0, ...(contentRootSx || {}) }}>
      {contentWrapper ? (
        <Box sx={{ bgcolor: contentBg, borderRadius: contentRadius, p: contentPadding, ...(contentSx || {}) }}>
          {children}
        </Box>
      ) : (
        children
      )}
    </DialogContent>

    {(actions || actionsSx) && (
      <DialogActions
        sx={{
          px: 0,
          pt: SPACE.section,
          pb: 0,
          gap: SPACE.row,
          justifyContent: 'flex-end',
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          '& > :not(:first-of-type)': { ml: 0 },
          '& .MuiButton-containedPrimary:not(.Mui-disabled)': {
            bgcolor: 'primary.dark',
            '&:hover': { bgcolor: 'primary.darker' },
          },
          ...(actionsSx || {}),
        }}
      >
        {actions}
      </DialogActions>
    )}
  </Dialog>
);

export default RoundedDialog;
